import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { dash } from "./dashboardTokens";

const ACCENT = {
  primary: "#2563eb",
  success: "#059669",
  warning: "#d97706",
};

/**
 * KPI tile — consistent icon treatment, tabular numbers, subtle accent rail.
 */
export default function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  accent = "primary",
  onClick,
  children,
}) {
  const color = ACCENT[accent] || ACCENT.primary;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        position: "relative",
        height: "100%",
        width: "100%",
        p: { xs: 2.25, sm: 2.75 },
        borderRadius: 2,
        border: `1px solid ${dash.border}`,
        bgcolor: dash.surface,
        boxShadow: dash.shadowSm,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: color,
          borderRadius: "4px 0 0 4px",
        },
        "&:hover": onClick
          ? {
              boxShadow: dash.shadowHover,
              borderColor: alpha(color, 0.35),
              transform: "translateY(-2px)",
            }
          : {},
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: { xs: 1.5, sm: 2 }, pl: 0.25 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: dash.textMuted,
              fontWeight: 600,
              letterSpacing: "0.08em",
              fontSize: 11,
              lineHeight: 1.4,
              display: "block",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            component="p"
            sx={{
              mt: 1,
              mb: 0,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "text.primary",
              lineHeight: 1.1,
              fontFeatureSettings: '"tnum"',
              fontSize: { xs: "1.65rem", sm: "1.85rem" },
            }}
          >
            {value}
          </Typography>
          {subtitle != null && subtitle !== "" && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                lineHeight: 1.45,
                fontSize: 13,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(color, 0.12),
              color,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        )}
      </Box>
      {children && (
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${dash.border}` }}>{children}</Box>
      )}
    </Paper>
  );
}
